import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { logout } from "features/auth/authSlice";
import { selectCurrentLanguage } from "features/language/languageSlice";

import { refreshToken, getAccessToken } from "components/utilities/sedra3/auth";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,

  prepareHeaders: (headers, { getState }) => {
    const token = getAccessToken();
    const state = getState();
    const currentLanguage = selectCurrentLanguage(state);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (currentLanguage) {
      headers.set("X-Accept-Language", currentLanguage);
    }
    return headers;
  },
});

// The API answers 401 when the token cannot be used (expired, revoked after a
// logout or a renewal, broken): renewing may help. A 403 is a signed-in user
// without the permission; renewing would not change it, so it goes to the
// calling component, which shows it, and the user stays signed in.

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  // The token this request leaves with (prepareHeaders reads the same one).
  const sentWith = getAccessToken();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    try {
      // Refused with a token another query has already replaced: retry with
      // the current one. Renewing again would revoke the pair just issued and
      // fail the queries retrying with it.
      const current = getAccessToken();
      const newToken =
        current && current !== sentWith ? current : await refreshToken();
      // Retry the original query with the new token
      if (newToken) {
        const retryArgs = {
          ...args,
          headers: {
            ...(args.headers || {}),
            Authorization: `Bearer ${newToken}`,
          },
        };

        result = await baseQuery(retryArgs, api, extraOptions);
      }
    } catch (error) {
      console.error("Token refresh failed", error);
      // The server refuses the session (logout, password reset, expiry). The
      // logout clears the stored tokens and the JWT mirror cookie — left in
      // place, nginx would keep verifying a token whose redis entry is gone and
      // the user would land in a redirect loop — and empties the API caches;
      // the signed-in pages then send the browser to the sign-in page.
      api.dispatch(logout());
    }
  }

  return result;
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    loginUser: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
    }),
    getBanks: builder.query({
      query: () => "/bank/list",
    }),
    getRolesByBank: builder.query({
      query: (bankId) => ({
        url: `/role/${bankId}/roles`,
        method: "GET",
        headers: {
          // Add any custom headers here if needed
        },
      }),
    }),
    registerUser: builder.mutation({
      query: (userDetails) => ({
        url: "/user",
        method: "POST",
        body: userDetails,
      }),
    }),
    openPasswordResetLink: builder.mutation({
      query: (linkToken) => ({
        url: "/login/reset-password/open",
        method: "POST",
        body: linkToken,
      }),
    }),
    resetPassword: builder.mutation({
      query: (passwordResetData) => ({
        url: "/login/reset-password",
        method: "POST",
        body: passwordResetData,
      }),
    }),
    passwordResetRequest: builder.mutation({
      query: (email) => ({
        url: "/login/password-reset-request",
        method: "POST",
        body: { email },
      }),
    }),
    changePassword: builder.mutation({
      query: (passwordChangeData) => ({
        url: "/login/change_password",
        method: "POST",
        body: passwordChangeData,
      }),
    }),
    logoutUser: builder.mutation({
      query: () => ({
        url: "/login/logout",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useGetBanksQuery,
  useGetRolesByBankQuery,
  useOpenPasswordResetLinkMutation,
  useResetPasswordMutation,
  usePasswordResetRequestMutation,
  useChangePasswordMutation,
  useLogoutUserMutation,
} = authApi;
